import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AutoAwesomeRounded,
  LogoutRounded,
  AddRounded,
  CloseRounded,
  TuneRounded,
} from '@mui/icons-material';
import { StudentOptions, TeacherClassItem, TeacherSubject } from '../types';
import {
  getStudentOptions,
  addTeacherClass,
  removeTeacherClass,
  updateTeacherSubjects,
} from '../services/api';
import { saveTeacherClassContext } from '../utils/teacherClassContext';
import { useTeacherAuth } from '../context/TeacherAuthContext';
import SubjectChipsSelect from './common/SubjectChipsSelect';

// School first: a facilitator can hold Grade 8-A in two schools, and those are
// two different classes (call 2026-08-26).
const classKey = (c: TeacherClassItem) => `${c.school ?? ''}__${c.grade}__${c.section ?? ''}`;
const classLabel = (c: TeacherClassItem) =>
  `Grade ${c.grade}${c.section ? ` · Section ${c.section}` : ''}`;

const TeacherView: React.FC = () => {
  const navigate = useNavigate();
  const { teacher, token, updateTeacher, logoutTeacher } = useTeacherAuth();

  const classes = useMemo(() => teacher?.classes ?? [], [teacher]);
  const [selectedKey, setSelectedKey] = useState<string>(
    classes.length === 1 ? classKey(classes[0]) : ''
  );
  const [error, setError] = useState('');

  // --- Manage classes & subjects state ---
  const [manageOpen, setManageOpen] = useState(false);
  const [options, setOptions] = useState<StudentOptions | null>(null);
  const [pendingGrade, setPendingGrade] = useState('');
  const [pendingSection, setPendingSection] = useState('');
  // Which school the new class belongs to. A facilitator can serve several
  // (call 2026-08-26), and "Grade 6-A" means nothing without saying where —
  // it can legitimately exist in two of their schools.
  const [pendingSchool, setPendingSchool] = useState('');
  // Split so each error renders next to the control that produced it — a single
  // bottom-of-panel error read as "add isn't working" because it was ~270px
  // below the + button (often below the fold).
  const [classError, setClassError] = useState('');
  const [subjectError, setSubjectError] = useState('');
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [pendingSubjects, setPendingSubjects] = useState<TeacherSubject[]>([]);
  const [pendingSubstitutes, setPendingSubstitutes] = useState<TeacherSubject[]>([]);
  const [isSavingSubjects, setIsSavingSubjects] = useState(false);
  const [subjectsSaved, setSubjectsSaved] = useState(false);

  useEffect(() => {
    let mounted = true;
    getStudentOptions()
      .then((o) => mounted && setOptions(o))
      .catch(() => mounted && setClassError('Failed to load class options.'));
    return () => {
      mounted = false;
    };
  }, []);

  // Seed the subjects editor from the live teacher profile.
  useEffect(() => {
    setPendingSubjects(teacher?.subjects ?? []);
    setPendingSubstitutes(teacher?.substitute_subjects ?? []);
  }, [teacher]);

  // Default the "add class" school to their own — the common case, and what
  // every single-school facilitator gets without seeing a picker at all.
  useEffect(() => {
    setPendingSchool((cur) => cur || teacher?.school || '');
  }, [teacher]);

  const handleOpen = (cls: TeacherClassItem) => {
    if (!teacher) return;
    const normalizedSection = cls.section ? cls.section.trim().toUpperCase() : null;
    // THE CLASS's school, not the facilitator's registered one. Stamping their
    // own school onto every class is what sent a multi-school facilitator to the
    // wrong school's students, syllabus and plans (2026-08-27).
    const classSchool = cls.school || teacher.school;
    saveTeacherClassContext({
      school: classSchool,
      grade: cls.grade,
      section: normalizedSection,
    });
    // Land in the chat — the coach is the teacher's primary surface.
    navigate('/ask-coach', {
      state: { school: classSchool, grade: cls.grade, section: normalizedSection ?? undefined },
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    const cls = classes.find((c) => classKey(c) === selectedKey);
    if (!cls) {
      setError('Please select a class to continue.');
      return;
    }
    handleOpen(cls);
  };

  const handleAddClass = async () => {
    setClassError('');
    if (!token) return;
    if (!pendingGrade) {
      setClassError('Pick a grade to add a class.');
      return;
    }
    const grade = Number(pendingGrade);
    const section = pendingSection.trim().toUpperCase() || null;
    const school = pendingSchool || teacher?.school || undefined;
    // Same grade + section in a DIFFERENT school is a different class, so the
    // "already added" check has to include the school.
    const sameSchool = (a?: string | null, b?: string | null) =>
      (a || teacher?.school || '').trim().toLowerCase() === (b || teacher?.school || '').trim().toLowerCase();
    if (classes.some((c) => c.grade === grade && (c.section || null) === section && sameSchool(c.school, school))) {
      setClassError('That class is already added in this school.');
      return;
    }
    setIsAdding(true);
    try {
      const updated = await addTeacherClass(token, { grade, section, school });
      updateTeacher(updated);
      setPendingGrade('');
      setPendingSection('');
    } catch (err) {
      setClassError(err instanceof Error ? err.message : 'Failed to add class.');
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveClass = async (cls: TeacherClassItem) => {
    setClassError('');
    if (!token) return;
    if (classes.length <= 1) {
      setClassError('You must keep at least one class.');
      return;
    }
    setBusyKey(classKey(cls));
    try {
      const updated = await removeTeacherClass(token, {
        grade: cls.grade, section: cls.section ?? null, school: cls.school ?? null,
      });
      updateTeacher(updated);
      setSelectedKey((cur) => (cur === classKey(cls) ? '' : cur));
    } catch (err) {
      setClassError(err instanceof Error ? err.message : 'Failed to remove class.');
    } finally {
      setBusyKey(null);
    }
  };

  const handleSaveSubjects = async () => {
    setSubjectError('');
    if (!token) return;
    if (pendingSubjects.length === 0) {
      setSubjectError('Pick at least one subject you teach.');
      return;
    }
    setIsSavingSubjects(true);
    setSubjectsSaved(false);
    try {
      const updated = await updateTeacherSubjects(token, {
        subjects: pendingSubjects,
        substitute_subjects: pendingSubstitutes,
      });
      updateTeacher(updated);
      setSubjectsSaved(true);
    } catch (err) {
      setSubjectError(err instanceof Error ? err.message : 'Failed to save subjects.');
    } finally {
      setIsSavingSubjects(false);
    }
  };

  const handleLogout = () => {
    logoutTeacher();
    navigate('/teacher-login');
  };

  // The schools this facilitator serves, each with its own classes. Their own
  // school first, then the rest alphabetically, so the list is stable.
  const schoolGroups = useMemo(() => {
    if (!teacher) return [] as { school: string; classes: TeacherClassItem[] }[];
    const groups = new Map<string, TeacherClassItem[]>();
    (teacher.schools ?? []).forEach((s) => groups.set(s, []));
    classes.forEach((c) => {
      const s = c.school || teacher.school;
      groups.set(s, [...(groups.get(s) ?? []), c]);
    });
    return Array.from(groups.entries())
      .map(([school, cls]) => ({
        school,
        classes: [...cls].sort((a, b) => a.grade - b.grade || (a.section ?? '').localeCompare(b.section ?? '')),
      }))
      .sort((a, b) =>
        a.school === teacher.school ? -1 : b.school === teacher.school ? 1 : a.school.localeCompare(b.school),
      );
  }, [teacher, classes]);
  const isMultiSchool = schoolGroups.length > 1;

  const fieldClass =
    'mt-1 block w-full rounded-xl border border-violet-200 bg-white px-3 py-3 text-base text-slate-900 shadow-sm focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200 sm:py-2 sm:text-sm disabled:cursor-not-allowed disabled:bg-violet-50';
  const labelClass = 'block text-sm font-medium text-slate-700';

  return (
    <div className="main-gradient-bg min-h-screen flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full space-y-6 rounded-3xl border border-violet-200 bg-white/95 p-8 shadow-sm">
        <div>
          <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-violet-100 text-violet-700">
            <AutoAwesomeRounded />
          </div>
          <h2 className="text-center text-2xl font-bold text-gray-900">
            {teacher?.name ? `Welcome, ${teacher.name}` : 'Teacher View'}
          </h2>
          {isMultiSchool ? (
            <p className="mt-2 flex flex-wrap items-center justify-center gap-1.5 text-sm text-violet-700">
              {schoolGroups.map((g) => (
                <span key={g.school} className="rounded-full bg-violet-50 px-2.5 py-0.5 font-semibold">
                  {g.school}
                </span>
              ))}
              <span className="text-slate-400">· {schoolGroups.length} schools</span>
            </p>
          ) : (
            <p className="mt-2 text-center text-sm text-violet-700">{teacher?.school}</p>
          )}
          <p className="mt-1 text-center text-sm text-slate-500">
            {isMultiSchool
              ? 'Choose a school, then the class you want.'
              : 'Choose a class to view its reports.'}
          </p>
        </div>

        {classes.length === 0 ? (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-center text-sm text-amber-700">
            No classes are linked to your account yet. Add one below.
          </p>
        ) : isMultiSchool ? (
          // One block per school. A facilitator with three schools needs to see
          // WHICH school a class is in before opening it — every figure inside
          // belongs to that school (call 2026-08-26).
          <div className="space-y-5">
            {schoolGroups.map((g) => (
              <div key={g.school}>
                <p className="mb-2 flex items-center gap-2 text-[11px] font-bold uppercase tracking-wide text-violet-500">
                  {g.school}
                  <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">
                    {g.classes.length} class{g.classes.length === 1 ? '' : 'es'}
                  </span>
                </p>
                {g.classes.length === 0 ? (
                  <p className="rounded-xl border border-dashed border-violet-200 px-3 py-2 text-xs text-slate-400">
                    No classes assigned in this school yet.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {g.classes.map((c) => (
                      <button
                        key={classKey(c)}
                        type="button"
                        onClick={() => handleOpen(c)}
                        className="flex w-full items-center justify-between rounded-2xl border border-violet-200 bg-white px-4 py-3 text-left text-sm font-medium text-slate-800 shadow-sm transition-colors hover:border-violet-300 hover:text-violet-700"
                      >
                        <span>{classLabel(c)}</span>
                        <span className="text-violet-500">View →</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : classes.length <= 4 ? (
          <div className="space-y-3">
            {classes.map((c) => (
              <button
                key={classKey(c)}
                type="button"
                onClick={() => handleOpen(c)}
                className="flex w-full items-center justify-between rounded-2xl border border-violet-200 bg-white px-4 py-3 text-left text-sm font-medium text-slate-800 shadow-sm transition-colors hover:border-violet-300 hover:text-violet-700"
              >
                <span>{classLabel(c)}</span>
                <span className="text-violet-500">View →</span>
              </button>
            ))}
          </div>
        ) : (
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="class" className={labelClass}>
                Class
              </label>
              <select
                id="class"
                name="class"
                className={fieldClass}
                value={selectedKey}
                onChange={(e) => setSelectedKey(e.target.value)}
              >
                <option value="">Select a class</option>
                {classes.map((c) => (
                  <option key={classKey(c)} value={classKey(c)}>
                    {classLabel(c)}
                  </option>
                ))}
              </select>
            </div>
            {error && (
              <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                {error}
              </p>
            )}
            <button
              type="submit"
              className="w-full flex justify-center rounded-xl border border-transparent bg-violet-500 px-4 py-3 text-sm font-medium text-white shadow-sm hover:bg-violet-600 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:ring-offset-2"
            >
              Continue
            </button>
          </form>
        )}

        {/* Manage classes & subjects */}
        <div className="rounded-2xl border border-violet-100 bg-violet-50/60 p-4">
          <button
            type="button"
            onClick={() => setManageOpen((o) => !o)}
            className="flex w-full items-center justify-between text-sm font-medium text-slate-700"
          >
            <span className="flex items-center gap-2">
              <TuneRounded fontSize="small" /> Manage classes & subjects
            </span>
            <span className="text-violet-500">{manageOpen ? 'Hide' : 'Edit'}</span>
          </button>

          {manageOpen && (
            <div className="mt-4 space-y-4">
              {classes.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {classes.map((c) => (
                    <span
                      key={classKey(c)}
                      className="inline-flex items-center gap-1 rounded-full bg-white px-3 py-1 text-sm text-indigo-700 shadow-sm"
                    >
                      {classLabel(c)}
                      {isMultiSchool && (c.school || teacher?.school) ? (
                        <span className="rounded-full bg-violet-50 px-1.5 text-[11px] font-semibold text-violet-600">
                          {c.school || teacher?.school}
                        </span>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => handleRemoveClass(c)}
                        disabled={busyKey === classKey(c) || classes.length <= 1}
                        className="text-indigo-400 hover:text-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
                        aria-label={`Remove ${classLabel(c)}`}
                      >
                        <CloseRounded fontSize="small" />
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {isMultiSchool && (
                <div>
                  <label htmlFor="add-class-school" className="text-xs font-medium text-slate-600">
                    Which school is this class in?
                  </label>
                  <select
                    id="add-class-school"
                    aria-label="School"
                    className={fieldClass}
                    value={pendingSchool}
                    onChange={(e) => setPendingSchool(e.target.value)}
                  >
                    {schoolGroups.map((g) => (
                      <option key={g.school} value={g.school}>
                        {g.school}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <select
                    aria-label="Grade"
                    className={fieldClass}
                    value={pendingGrade}
                    onChange={(e) => setPendingGrade(e.target.value)}
                    disabled={!options}
                  >
                    <option value="">Grade</option>
                    {options?.grades.map((g) => (
                      <option key={g} value={g}>
                        Grade {g}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex-1">
                  <select
                    aria-label="Section"
                    className={fieldClass}
                    value={pendingSection}
                    onChange={(e) => setPendingSection(e.target.value)}
                    disabled={!options}
                  >
                    <option value="">No section</option>
                    {options?.sections.map((s) => (
                      <option key={s} value={s}>
                        Section {s}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={handleAddClass}
                  disabled={isAdding || !options}
                  className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-violet-100 text-violet-700 hover:bg-violet-200 disabled:cursor-not-allowed disabled:opacity-50 sm:h-[42px] sm:w-[42px]"
                  aria-label="Add class"
                >
                  <AddRounded />
                </button>
              </div>

              {pendingGrade && (
                <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                  You picked <span className="font-semibold">Grade {pendingGrade}{pendingSection ? ` · ${pendingSection}` : ''}</span> — tap the <span className="font-semibold">+</span> to add it. ("Save subjects" below only saves subjects.)
                </p>
              )}

              {classError && (
                <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                  {classError}
                </p>
              )}

              {/* Subjects */}
              <div className="border-t border-violet-100 pt-4">
                <SubjectChipsSelect
                  value={pendingSubjects}
                  substitutes={pendingSubstitutes}
                  onChange={(next, nextSubs) => {
                    setPendingSubjects(next);
                    setPendingSubstitutes(nextSubs);
                    setSubjectsSaved(false);
                  }}
                  disabled={isSavingSubjects}
                />
                <div className="mt-3 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleSaveSubjects}
                    disabled={isSavingSubjects}
                    className={`rounded-xl px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 ${
                      subjectsSaved ? 'bg-emerald-500 hover:bg-emerald-600' : 'bg-violet-500 hover:bg-violet-600'
                    }`}
                  >
                    {isSavingSubjects ? 'Saving…' : subjectsSaved ? 'Saved ✓' : 'Save subjects'}
                  </button>
                  {subjectsSaved && !isSavingSubjects && (
                    <span className="text-sm font-medium text-emerald-600">Subjects updated</span>
                  )}
                </div>
                {subjectError && (
                  <p className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                    {subjectError}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-center gap-6">
          <button
            type="button"
            onClick={() => navigate('/pdf-topics')}
            className="flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-violet-700"
          >
            📄 PDF Topic Extractor
          </button>
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-violet-700"
          >
            <LogoutRounded fontSize="small" /> Log out
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeacherView;
